/**
 * Brands you carry (Priya, flag `distributor_carry`, MOT-159): a distributor
 * or wholesaler asks to carry brands it does not own, with a line card,
 * invoice or feed as evidence, and sees what Alex decided. Carry is not a
 * claim: it opens the catalog browser and warehouse matching for those
 * brands and grants no MAP or rebate authority.
 */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useToast } from '../contexts/ToastContext';
import { useBrandRegistry } from '../hooks/useBrandRegistry';
import { EmptyState, FormDialog, InlineMessage, LoadingState, PageContainer, PageHeader, SectionCard, StatusChip } from '../components/ui';
import type { StatusTone } from '../components/ui';
import { ValidatedTextField } from '../components/ui/ValidatedTextField';
import { validateUrl } from '../utils/inputValidators';
import { PROOF_ACCEPTED_TYPES, PROOF_MAX_FILE_BYTES, isAcceptedProofFileType } from '../utils/brandAuthorization';
import {
  attachVendorCarryProof,
  carryProofStoragePath,
  createVendorCarryRequest,
  listVendorCarryRequests,
  VENDOR_CARRY_REQUESTS_QUERY_KEY,
  type CarryEvidenceKind,
  type CarryRequest,
  type CarryRequestStatus,
} from '../services/carryRequestsApi';

const NS = 'vendor.carryRequests';
const EVIDENCE_KINDS: CarryEvidenceKind[] = ['line_card', 'invoice', 'warehouse_feed'];
const MAX_BRANDS = 50;

const STATUS_TONE: Record<CarryRequestStatus, StatusTone> = {
  pending: 'caution',
  approved: 'positive',
  rejected: 'problem',
  revoked: 'neutral',
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

export const VendorCarryRequestsPage: React.FC = () => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const registry = useBrandRegistry();

  const { data, isLoading, isError } = useQuery({
    queryKey: VENDOR_CARRY_REQUESTS_QUERY_KEY,
    queryFn: listVendorCarryRequests,
  });

  const [open, setOpen] = useState(false);
  const [brandIds, setBrandIds] = useState<string[]>([]);
  const [evidenceKind, setEvidenceKind] = useState<CarryEvidenceKind>('line_card');
  const [evidenceLink, setEvidenceLink] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const requests = data?.requests || [];
  const eligibility = data?.eligibility;

  const brandNameById = useMemo(() => {
    const map = new Map<string, string>();
    (registry.data || []).forEach((b) => {
      if (b.slug) map.set(b.slug.toLowerCase(), b.name);
      if (b.id) map.set(String(b.id).toLowerCase(), b.name);
    });
    requests.forEach((r) => r.brandIds.forEach((id, i) => map.set(id.toLowerCase(), r.brandNames[i] || id)));
    return map;
  }, [registry.data, requests]);
  const nameOf = (id: string) => brandNameById.get(id.toLowerCase()) || id;

  // Brands already approved or pending cannot be asked for again; nor the vendor's own brand.
  const unavailable = useMemo(() => {
    const set = new Set<string>((eligibility?.carriedBrandIds || []).map((id) => id.toLowerCase()));
    requests.filter((r) => r.status === 'pending' || r.status === 'approved').forEach((r) => r.brandIds.forEach((id) => set.add(id.toLowerCase())));
    if (eligibility?.ownBrandId) set.add(eligibility.ownBrandId.toLowerCase());
    return set;
  }, [eligibility, requests]);

  const options = useMemo(
    () =>
      (registry.data || [])
        .filter((b) => b.slug && b.name && !unavailable.has(b.slug.toLowerCase()))
        .map((b) => ({ id: b.slug, label: b.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [registry.data, unavailable],
  );

  const carried = eligibility?.carriedBrandIds || [];
  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  const reset = () => {
    setBrandIds([]);
    setEvidenceKind('line_card');
    setEvidenceLink('');
    setNote('');
    setFile(null);
    setFileError(null);
    setSubmitError(null);
  };

  const onPickFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0] || null;
    event.target.value = '';
    if (!picked) return;
    if (!isAcceptedProofFileType(picked)) {
      setFileError(t(`${NS}.form.fileType`));
      return;
    }
    if (picked.size > PROOF_MAX_FILE_BYTES) {
      setFileError(t(`${NS}.form.fileSize`));
      return;
    }
    setFileError(null);
    setFile(picked);
  };

  const submit = useMutation({
    mutationFn: async () => {
      const created = await createVendorCarryRequest({
        brandIds,
        evidenceKind,
        evidenceLink: evidenceLink.trim() || undefined,
        note: note.trim() || undefined,
      });
      if (file) {
        // The record is filed; a failed upload leaves it pending without evidence
        // and says so, rather than losing the request.
        try {
          const storagePath = carryProofStoragePath(created.vendorOrganizationId, created.id, file.name);
          await uploadBytes(storageRef(storage, storagePath), file, { contentType: file.type || 'application/pdf' });
          await attachVendorCarryProof(created.id, { fileName: file.name, storagePath, contentType: file.type || undefined });
        } catch (error: any) {
          return { created, proofError: error?.message || 'upload failed' };
        }
      }
      return { created, proofError: null as string | null };
    },
    onSuccess: ({ proofError }) => {
      queryClient.invalidateQueries({ queryKey: VENDOR_CARRY_REQUESTS_QUERY_KEY });
      setOpen(false);
      reset();
      if (proofError) showToast(t(`${NS}.form.proofFailed`, { reason: proofError }), 'warning');
      else showToast(t(`${NS}.form.sent`), 'success');
    },
    onError: (error: any) => setSubmitError(error?.message || t(`${NS}.error`)),
  });

  const linkError = evidenceLink.trim() ? validateUrl(evidenceLink) : null;
  const canSubmit = brandIds.length > 0 && brandIds.length <= MAX_BRANDS && !linkError && !fileError;

  return (
    <PageContainer>
      <PageHeader
        title={t(`${NS}.title`)}
        description={t(`${NS}.description`)}
        actions={
          eligibility?.eligible ? (
            <Button variant="contained" onClick={() => { reset(); setOpen(true); }}>
              {t(`${NS}.request`)}
            </Button>
          ) : undefined
        }
      />

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <InlineMessage severity="error">{t(`${NS}.loadError`)}</InlineMessage>
      ) : (
        <Stack spacing={2}>
          {eligibility && !eligibility.eligible && (
            <InlineMessage severity="info">
              {t(`${NS}.ineligible.${eligibility.reason || 'not_vendor'}`)}
            </InlineMessage>
          )}

          <SectionCard
            title={t(`${NS}.book.title`)}
            subtitle={t(`${NS}.book.coverage`, { approved: carried.length, pending: pendingCount })}
            dense
            padded
          >
            {carried.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t(`${NS}.book.empty`, { brand: eligibility?.ownBrandName || t(`${NS}.book.yourBrand`) })}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {carried.map((id) => (
                  <Chip key={id} label={id === '*' ? t(`${NS}.book.wholeCatalog`) : nameOf(id)} size="small" />
                ))}
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {t(`${NS}.book.note`)}
            </Typography>
          </SectionCard>

          <SectionCard title={t(`${NS}.list.title`)} dense padded>
            {requests.length === 0 ? (
              <EmptyState
                title={t(`${NS}.list.emptyTitle`)}
                description={t(`${NS}.list.emptyDescription`)}
                size="compact"
                actionLabel={eligibility?.eligible ? t(`${NS}.request`) : undefined}
                onAction={eligibility?.eligible ? () => { reset(); setOpen(true); } : undefined}
              />
            ) : (
              <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                {requests.map((r: CarryRequest) => (
                  <Box key={r.id} sx={{ py: 1.5 }}>
                    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2">{r.brandNames.join(', ') || r.brandIds.join(', ')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t(`${NS}.evidence.${r.evidence.kind}`)} · {t(`${NS}.list.sent`, { date: formatDate(r.createdAt) })}
                          {r.source === 'backfill' ? ` · ${t(`${NS}.list.backfill`)}` : ''}
                        </Typography>
                      </Box>
                      <StatusChip label={t(`${NS}.status.${r.status}`)} tone={STATUS_TONE[r.status]} size="small" dot />
                    </Stack>
                    {(r.evidence.link || r.evidence.proof) && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {r.evidence.proof && (
                          <Box component="a" href={r.evidence.proof.url || undefined} target="_blank" rel="noopener noreferrer" sx={{ mr: 1 }}>
                            {r.evidence.proof.fileName}
                          </Box>
                        )}
                        {r.evidence.link && (
                          <Box component="a" href={r.evidence.link} target="_blank" rel="noopener noreferrer">
                            {r.evidence.link}
                          </Box>
                        )}
                      </Typography>
                    )}
                    {r.evidence.note && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{r.evidence.note}</Typography>
                    )}
                    {r.decisionNote && r.status !== 'pending' && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t(`${NS}.list.decisionNote`, { note: r.decisionNote })}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Stack>
      )}

      <FormDialog
        open={open}
        title={t(`${NS}.form.title`)}
        onClose={() => { setOpen(false); reset(); }}
        onSubmit={() => submit.mutate()}
        submitLabel={t(`${NS}.form.submit`)}
        submitDisabled={!canSubmit}
        loading={submit.isPending}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">{t(`${NS}.form.help`)}</Typography>
          <Autocomplete
            multiple
            options={options}
            loading={registry.isLoading}
            value={options.filter((o) => brandIds.includes(o.id))}
            onChange={(_event, next) => setBrandIds(next.map((o) => o.id).slice(0, MAX_BRANDS))}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(`${NS}.form.brands`)}
                helperText={t(`${NS}.form.brandsHelp`, { count: brandIds.length, max: MAX_BRANDS })}
              />
            )}
          />
          <FormControl>
            <Typography variant="overline">{t(`${NS}.form.evidence`)}</Typography>
            <RadioGroup value={evidenceKind} onChange={(e) => setEvidenceKind(e.target.value as CarryEvidenceKind)}>
              {EVIDENCE_KINDS.map((kind) => (
                <FormControlLabel key={kind} value={kind} control={<Radio size="small" />} label={t(`${NS}.evidence.${kind}`)} />
              ))}
            </RadioGroup>
          </FormControl>
          <ValidatedTextField
            label={t(`${NS}.form.link`)}
            value={evidenceLink}
            onChange={(e) => setEvidenceLink(e.target.value)}
            validate={(value) => (value.trim() ? validateUrl(value) : null)}
            fullWidth
            placeholder="https://"
            helperText={t(`${NS}.form.linkHelp`)}
          />
          <Box>
            <Button variant="outlined" component="label" size="small">
              {file ? t(`${NS}.form.replaceFile`) : t(`${NS}.form.chooseFile`)}
              <input type="file" hidden accept={PROOF_ACCEPTED_TYPES} onChange={onPickFile} />
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {file ? file.name : t(`${NS}.form.fileHelp`)}
            </Typography>
            {fileError && <Alert severity="error" sx={{ mt: 1 }}>{fileError}</Alert>}
          </Box>
          <TextField
            label={t(`${NS}.form.note`)}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            inputProps={{ maxLength: 1000 }}
          />
          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Stack>
      </FormDialog>
    </PageContainer>
  );
};

export default VendorCarryRequestsPage;
